import {skipToken} from '@reduxjs/toolkit/query';
import {FormField, Separator, TabsHeader, type FormControl} from 'platform/components';
import {Grid, GridItem, Heading, Show, Space, Text, VStack} from 'platform/foundation';

import {useWatch, type UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, noop} from 'ramda-adjunct';

import {
  useGetCheckoutProfileQuery,
  useGetCheckoutProfilesQuery,
  type CheckoutProfileRule,
  type CheckoutProfileRuleWrite,
  type CheckoutProfileError,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {SettingsTemplate, SettingsFooter} from '@dms/settings/shared';
import {useBranches} from '@dms/shared';

import {EMPTY_CHECKOUT_RULE_CONDITIONS} from '../../../constants/EMPTY_CHECKOUT_RULE_CONDITIONS';
import {getCheckoutProfileIssueMessage} from '../../../utils/getCheckoutProfileIssueMessage';
import {CheckoutRuleFormSchema} from '../types/CheckoutRuleFormSchema';
import type {CheckoutRuleFormValues} from '../types/CheckoutRuleFormValues';
import {CheckoutRuleConditions} from './CheckoutRuleConditions';
import {CheckoutRuleFeedback} from './CheckoutRuleFeedback';
import {CheckoutRulePreview} from './CheckoutRulePreview';
import {CheckoutRuleSourceTypes} from './CheckoutRuleSourceTypes';

interface Props {
  control: FormControl<CheckoutRuleFormValues>;
  formApi: UseFormReturn<CheckoutRuleFormValues>;
  rule?: CheckoutProfileRule;
  contextProfileId?: string;
  returnPath: string;
  onDiscard: () => void;
  serverResponse?: {errors: CheckoutProfileError[]; values: CheckoutProfileRuleWrite};
}

export function CheckoutRuleFormContent(props: Props) {
  const [name, branchId, checkoutProfileId, sourceType, isActive, priority, conditions] = useWatch({
    control: props.control,
    name: [
      'name',
      'branchId',
      'checkoutProfileId',
      'sourceType',
      'isActive',
      'priority',
      'conditions',
    ],
  });

  const values: CheckoutRuleFormValues = {
    name,
    branchId,
    checkoutProfileId,
    sourceType,
    isActive,
    priority,
    conditions,
  };

  const parsed = isNotNil(props.serverResponse)
    ? CheckoutRuleFormSchema.safeParse(values)
    : undefined;
  const errors =
    parsed?.success === true &&
    isNotNil(props.serverResponse) &&
    JSON.stringify(parsed.data) === JSON.stringify(props.serverResponse.values)
      ? props.serverResponse.errors.map((error) => ({
          ...error,
          path: error.path.replace(/\[(\d+)\]/g, '.$1'),
          message: getCheckoutProfileIssueMessage(error.code, error.message),
        }))
      : [];

  const conditionErrors = errors.filter(
    (error) => error.path === 'conditions' || error.path.startsWith('conditions.')
  );

  const generalErrors = errors.filter((error) => !conditionErrors.includes(error));
  const feedbackErrors = [
    ...generalErrors,
    ...(sourceType === null && props.formApi.formState.errors.sourceType
      ? [
          {
            code: 'LOCAL_SOURCE_TYPE_REQUIRED',
            path: 'sourceType',
            message: i18n.t('entity.checkout.validation.sourceTypeRequired'),
          },
        ]
      : []),
  ];

  const fieldErrors = Object.fromEntries(generalErrors.map((error) => [error.path, error.message]));
  const branches = useBranches();
  const profiles = useGetCheckoutProfilesQuery(isNotNilOrEmpty(branchId) ? {branchId} : skipToken);
  const contextProfile = useGetCheckoutProfileQuery(
    isNil(props.rule) && isNotNil(props.contextProfileId)
      ? {checkoutProfileId: props.contextProfileId}
      : skipToken
  );

  const profileOptions = (profiles.currentData ?? [])
    .filter((profile) => profile.branchId === branchId)
    .map((profile) => ({
      value: profile.id,
      label: profile.name,
    }));

  const fallbackProfile = isNotNil(props.rule)
    ? {
        id: props.rule.checkoutProfileId,
        name: props.rule.checkoutProfile.name,
        branchId: props.rule.branchId,
      }
    : contextProfile.currentData;

  if (
    fallbackProfile?.branchId === branchId &&
    !profileOptions.some((option) => option.value === fallbackProfile.id)
  ) {
    profileOptions.push({
      value: fallbackProfile.id,
      label: fallbackProfile.name,
    });
  }

  return (
    <SettingsTemplate
      header={{
        title: props.rule?.name ?? i18n.t('entity.checkout.labels.newCheckoutRule'),
        breadcrumbs: [{label: i18n.t('entity.automationRule.list.title'), href: props.returnPath}],
      }}
      isCreating={isNil(props.rule)}
      data-testid="checkout-rule-detail"
    >
      <VStack>
        <VStack>
          <CheckoutRuleFeedback errors={feedbackErrors} />
          <Show when={feedbackErrors.length > 0}>
            <Space vertical={4} />
          </Show>
          <Grid columns={4} spacing={4} align="flex-start">
            <GridItem span={2}>
              <FormField
                control={props.control}
                name="name"
                errorMessage={fieldErrors.name}
                type="text"
                label={i18n.t('entity.interest.labels.ruleName')}
                maxLength={100}
                isRequired
              />
            </GridItem>
            <GridItem>
              <FormField
                control={props.control}
                name="priority"
                errorMessage={fieldErrors.priority}
                type="number"
                label={i18n.t('entity.warehouse.labels.priority')}
                isRequired
                minStepperValue={0}
                decimalPlaces={0}
                isStepperVisible
                helperText={i18n.t('entity.checkout.labels.rulePriorityDescription')}
              />
            </GridItem>
            <GridItem>
              <Space vertical={6} />
              <FormField
                control={props.control}
                name="isActive"
                type="switch"
                label={i18n.t('general.labels.active')}
              />
            </GridItem>
            <GridItem span={2}>
              <FormField
                control={props.control}
                name="branchId"
                errorMessage={fieldErrors.branchId}
                type="choice"
                label={i18n.t('entity.branch.labels.branch')}
                options={branches.branchOptions}
                isLoading={branches.isLoading}
                isRequired
                isNotClearable
                onChange={() => {
                  props.formApi.setValue('checkoutProfileId', '', {shouldDirty: true});
                  props.formApi.setValue(
                    'conditions',
                    {...EMPTY_CHECKOUT_RULE_CONDITIONS},
                    {shouldDirty: true}
                  );
                }}
              />
            </GridItem>
            <GridItem span={2}>
              <FormField
                control={props.control}
                name="checkoutProfileId"
                errorMessage={fieldErrors.checkoutProfileId}
                type="choice"
                label={i18n.t('entity.checkout.labels.checkoutProfile')}
                options={profileOptions}
                isLoading={profiles.isFetching || contextProfile.isFetching}
                isDisabled={isNilOrEmpty(branchId) || profiles.isError}
                isRequired
                isNotClearable
                helperText={i18n.t('entity.checkout.labels.ruleProfileDescription')}
              />
            </GridItem>
          </Grid>
        </VStack>
        <CheckoutRuleSourceTypes control={props.control} formApi={props.formApi} />
        <Show when={isNotNil(sourceType) ? {...values, sourceType} : null}>
          {(sourceValues) => (
            <>
              <Separator />
              <Heading size={4}>{i18n.t('entity.checkout.labels.sourceSettings')}</Heading>
              <Space vertical={1} />
              <Text size="small" color="secondary">
                {i18n.t('entity.checkout.labels.ruleSourceSettingsDescription')}
              </Text>
              <Space vertical={4} />
              <TabsHeader
                activeTabId={sourceValues.sourceType}
                tabs={[
                  {
                    id: sourceValues.sourceType,
                    title:
                      sourceValues.sourceType === 'service-order'
                        ? i18n.t('page.workshop.labels.serviceOrders')
                        : i18n.t('entity.warehouse.labels.directSales'),
                  },
                ]}
                onChange={noop}
              />
              <Space vertical={6} />
              <CheckoutRuleConditions
                control={props.control}
                formApi={props.formApi}
                values={sourceValues}
                errors={conditionErrors}
              />
              <CheckoutRulePreview values={sourceValues} ruleId={props.rule?.id} />
            </>
          )}
        </Show>
        <SettingsFooter
          actions={[
            {
              type: 'button',
              variant: 'secondary',
              title: i18n.t('general.actions.discardChanges'),
              onClick: props.onDiscard,
            },
            {
              type: 'form-button',
              control: props.control,
              title: i18n.t('general.actions.saveChanges'),
              buttonType: 'submit',
            },
          ]}
        />
      </VStack>
    </SettingsTemplate>
  );
}
